import { getMessaging, getToken, onMessage, Messaging, deleteToken } from 'firebase/messaging';
import { getFirebaseApp } from './firebase';
import { headerService } from './headers';
import { API_CONFIG } from '@/config/api';

// Tipos para las notificaciones
export interface PushNotification {
  title: string;
  body: string;
  data?: Record<string, string>;
  priority?: 'high' | 'normal';
}

export interface NotificationData {
  type: 'new_order' | 'order_update' | 'waiter_request';
  orderId?: string;
  restaurantId?: string;
  tableId?: string;
  requestId?: string;
  status?: string;
  timestamp: string;
}

// Clase para manejar notificaciones push
class NotificationService {
  private messaging: Messaging | null = null;
  private isInitialized = false;
  private permissionRequested = false;
  private currentToken: string | null = null;
  private swRegistration: ServiceWorkerRegistration | null = null;
  private lastUserId: string | null = null;
  private notificationCallback?: (notification: PushNotification, data: NotificationData) => void;
  private notificationCache: Map<string, number> = new Map(); // Cache para deduplicación

  constructor() {
    this.initialize();
  }

  /**
   * Inicializa el servicio de notificaciones
   */
  private async initialize() {
    try {
      // Verificar si el navegador soporta notificaciones
      if (typeof Notification === 'undefined' || !('Notification' in window)) {
        console.warn('⚠️ Este navegador no soporta notificaciones push');
        return;
      }

      // Verificar si Firebase está disponible
      if (!getFirebaseApp()) {
        console.warn('Firebase no está inicializado');
        return;
      }

      // Inicializar Firebase Messaging
      this.messaging = getMessaging(getFirebaseApp());
      this.isInitialized = true;

      if (import.meta.env.DEV) console.log('Servicio de notificaciones OK ...');
      
      // Intentar preparar el Service Worker y configurar verificación de rotación de token
      this.registerServiceWorker().catch(() => {});
      this.setupTokenAutoRefresh();

      // Comando de consola para resetear notificaciones (solo desarrollo)
      if (import.meta.env.DEV) {
        (window as any).resetNotifications = () => {
          localStorage.removeItem('notification-banner-dismissed');
          window.location.reload();
          console.log('🔄 Notificaciones reseteadas. Recarga la página para ver el banner.');
        };
        console.log('🛠️ Comando disponible: resetNotifications() - Para resetear notificaciones');
      }
    } catch (error) {
      console.error('❌ Error inicializando servicio de notificaciones:', error);
    }
  }

  /**
   * Solicita permisos para notificaciones push
   */
  async requestPermission(userId?: string): Promise<boolean> {
    try {
      if (!this.isInitialized) {
        console.warn('Servicio de notificaciones no inicializado');
        return false;
      }

      if (userId) {
        this.lastUserId = userId;
      }

      // Verificar si ya se solicitó permiso y tenemos token
      if (this.permissionRequested && this.currentToken) {
        console.log('⚠️ Permisos ya solicitados y token FCM ya obtenido');
        return true;
      }

      // Verificar si la API Notification está disponible
      if (typeof Notification === 'undefined') {
        console.warn('⚠️ API Notification no disponible en este navegador');
        return false;
      }

      // Verificar si ya tenemos permisos
      if (Notification.permission === 'granted') {
        console.log('✅ Permisos ya otorgados');
        
        // Obtener token FCM solo si no tenemos uno
        if (!this.currentToken) {
          await this.getFCMToken(userId);
        }
        
        // Configurar listener para notificaciones en primer plano
        this.setupForegroundListener();
        
        this.permissionRequested = true;
        return true;
      }

      // Solicitar permisos del navegador
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        console.log('✅ Permisos de notificación concedidos');
        
        // Obtener token FCM solo si no tenemos uno
        if (!this.currentToken) {
          await this.getFCMToken(userId);
        }
        
        // Configurar listener para notificaciones en primer plano
        this.setupForegroundListener();
        
        this.permissionRequested = true;
        return true;
      } else {
        console.warn('❌ Permisos de notificación denegados');
        return false;
      }
    } catch (error) {
      console.error('❌ Error solicitando permisos de notificación:', error);
      return false;
    }
  }

  /**
   * Obtiene el token FCM del usuario
   */
  private async getFCMToken(userId?: string): Promise<string | null> {
    try {
      if (!this.messaging) {
        console.warn('Firebase Messaging no está disponible');
        return null;
      }

      if (userId) {
        this.lastUserId = userId;
      }

      // Registrar explícitamente el Service Worker de Firebase y usarlo en getToken
      const reg = await this.registerServiceWorker();

      // Obtener token FCM usando el Service Worker de Firebase
      const token = await getToken(this.messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
        // Atar el token al SW correcto para evitar problemas de scope/heurísticas
        serviceWorkerRegistration: reg || undefined
      });

      if (token) {
        console.log('✅ Token FCM obtenido:', token);
        
        // Guardar token para evitar múltiples registros
        this.currentToken = token;
        
        // Enviar token al backend para almacenarlo
        if (userId) {
          await this.sendTokenToBackend(token, userId);
        } else {
          console.warn('⚠️ No se proporcionó userId, no se enviará token al backend');
        }
        
        return token;
      } else {
        console.warn('❌ No se pudo obtener token FCM');
        return null;
      }
    } catch (error) {
      console.error('❌ Error obteniendo token FCM:', error);
      return null;
    }
  }

  /**
   * Registra el Service Worker de Firebase de forma explícita
   */
  private async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    try {
      if (!('serviceWorker' in navigator)) {
        console.warn('⚠️ Service Workers no soportados por el navegador');
        return null;
      }
      // Reusar si ya existe
      if (this.swRegistration) {
        return this.swRegistration;
      }
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
      this.swRegistration = registration;
      if (import.meta.env.DEV) {
        console.log('🧩 SW de Firebase registrado explícitamente:', registration.scope);
      }
      return registration;
    } catch (error) {
      console.error('❌ Error registrando Service Worker de Firebase:', error);
      return null;
    }
  }

  /**
   * Sincroniza/envía el token al backend (en login o refresh)
   */
  async syncFCMToken(userId?: string): Promise<void> {
    try {
      if (!this.isInitialized) return;
      // Si no hay permisos, no forzar diálogo aquí
      if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
        if (import.meta.env.DEV) console.log('🔒 Notificaciones no concedidas; se omite syncFCMToken');
        return;
      }
      await this.getFCMToken(userId || this.lastUserId || undefined);
    } catch (error) {
      console.error('❌ Error sincronizando token FCM:', error);
    }
  }

  /**
   * Maneja auto-verificación de rotación de token
   * Reintenta obtener el token al recuperar visibilidad o al foco
   */
  private setupTokenAutoRefresh(): void {
    const handler = async () => {
      try {
        if (!this.messaging) return;
        const reg = this.swRegistration || await this.registerServiceWorker();
        const freshToken = await getToken(this.messaging, {
          vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: reg || undefined
        });
        if (freshToken && freshToken !== this.currentToken) {
          console.log('🔄 Token FCM rotó; reenviando al backend');
          this.currentToken = freshToken;
          if (this.lastUserId) {
            await this.sendTokenToBackend(freshToken, this.lastUserId);
          }
        }
      } catch (error) {
        console.warn('⚠️ Error comprobando rotación de token:', error);
      }
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        handler();
      }
    });
    window.addEventListener('focus', handler);
  }

      /**
     * Envía el token FCM al backend
     */
    private async sendTokenToBackend(token: string, userId: string): Promise<void> {
        try {
            console.log('📤 Enviando token FCM al backend para usuario:', userId);
            
            // Usar headers estándar de la aplicación
            const headers = headerService.getHeaders('authenticated');
            
            // Obtener token de autorización del localStorage o contexto
            const authToken = localStorage.getItem('authToken') || localStorage.getItem('firebase:authUser:AIzaSyB...:web:token');
            
            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
                console.log('🔐 Token de autorización agregado a headers');
            } else {
                console.warn('⚠️ No se encontró token de autorización');
            }
            
            // Enviar token al backend
            const apiUrl = `${API_CONFIG.BASE_URL}/fcm-tokens/${userId}`;
            
            console.log('🌐 Enviando a URL:', apiUrl);
            console.log('🌐 URL completa:', window.location.origin + apiUrl);
            
            const response = await fetch(apiUrl, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ tokenFcm: token })
            });
            
            if (response.ok) {
                const result = await response.json();
                console.log('✅ Token FCM enviado al backend exitosamente:', result);
            } else {
                console.error('❌ Error enviando token FCM al backend:', response.status, response.statusText);
            }
            
        } catch (error) {
            console.error('❌ Error enviando token FCM al backend:', error);
        }
    }

  /**
   * Elimina el token en backend (y local) al cerrar sesión
   */
  async clearFCMToken(userId: string): Promise<void> {
    try {
      // Eliminar token local si es posible
      if (this.messaging) {
        try {
          const deleted = await deleteToken(this.messaging);
          if (deleted) {
            if (import.meta.env.DEV) console.log('🧹 Token FCM local eliminado');
          }
        } catch (_) {
          // No bloquear por fallo local
        }
      }
      // Eliminar en backend
      const headers = headerService.getHeaders('authenticated');
      const apiUrl = `${API_CONFIG.BASE_URL}/fcm-tokens/${userId}`;
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers
      });
      if (response.ok) {
        console.log('✅ Token FCM eliminado en backend');
      } else {
        console.warn('⚠️ No se pudo eliminar token FCM en backend:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ Error eliminando token FCM en backend:', error);
    } finally {
      this.currentToken = null;
    }
  }

  /**
   * Configura el listener para notificaciones en primer plano
   */
  private setupForegroundListener(): void {
    if (!this.messaging) {
      console.warn('⚠️ Firebase Messaging no está disponible para listener en primer plano');
      return;
    }

    onMessage(this.messaging, (payload) => {
      console.log('📱 Notificación recibida en primer plano:', payload);
      
      const { notification, data } = payload;
      
      if (notification) {
        // Crear tag único para deduplicación
        const notificationTag = this.createNotificationTag(data);
        
        // Verificar si ya se mostró una notificación similar recientemente
        if (this.isDuplicateNotification(notificationTag)) {
          console.log('🔄 Notificación duplicada detectada en primer plano, no mostrando:', notificationTag);
          return;
        }
        
        // Marcar como mostrada
        this.markNotificationAsShown(notificationTag);
        
        // Solo mostrar notificación local si la app está realmente en primer plano
        // y no hay notificaciones del sistema mostrándose
        if (document.visibilityState === 'visible' && !this.hasSystemNotification()) {
          // Validar que title y body existan antes de mostrar la notificación
          if (notification.title && notification.body) {
            this.showLocalNotification(notification.title, notification.body, data, notificationTag);
          }
        }
        
        // Llamar callback si está configurado
        if (this.notificationCallback && data) {
          // Convertir data de Firebase al tipo NotificationData
          const notificationData: NotificationData = {
            type: (data.type as 'new_order' | 'order_update' | 'waiter_request') || 'new_order',
            orderId: data.orderId,
            restaurantId: data.restaurantId,
            tableId: data.tableId,
            requestId: data.requestId,
            status: data.status,
            timestamp: data.timestamp || new Date().toISOString(),
          };
          
          this.notificationCallback(notification as PushNotification, notificationData);
        }
      }
    });

    console.log('✅ Listener de notificaciones en primer plano configurado');
  }

  /**
   * Crea un tag único para la notificación
   */
  private createNotificationTag(data: any): string {
    if (data?.orderId) {
      return `order_${data.orderId}_${data.type || 'default'}`;
    } else if (data?.tableId) {
      return `table_${data.tableId}_${data.type || 'default'}`;
    } else if (data?.type) {
      return `type_${data.type}_${Date.now()}`;
    } else {
      return `default_${Date.now()}`;
    }
  }

  /**
   * Verifica si es una notificación duplicada
   */
  private isDuplicateNotification(tag: string): boolean {
    const now = Date.now();
    const lastShown = this.notificationCache.get(tag);
    
    if (lastShown && (now - lastShown) < 5000) { // 5 segundos de ventana
      return true;
    }
    
    return false;
  }

  /**
   * Marca una notificación como mostrada
   */
  private markNotificationAsShown(tag: string): void {
    this.notificationCache.set(tag, Date.now());
    
    // Limpiar cache antiguo después de 1 minuto
    setTimeout(() => {
      this.notificationCache.delete(tag);
    }, 60000);
  }

  /**
   * Verifica si hay notificaciones del sistema mostrándose
   */
  private hasSystemNotification(): boolean {
    // En iOS, si hay notificaciones del sistema, no mostrar locales
    // Esto evita duplicados
    return false; // Por ahora, siempre permitir notificaciones locales
  }

  /**
   * Muestra una notificación local
   */
  private showLocalNotification(title: string, body: string, data?: any, tag?: string): void {
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        data,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: tag || 'ots-notification', // Usar tag único si se proporciona
        requireInteraction: false
      });

      // Manejar clic en la notificación
      notification.onclick = () => {
        window.focus();
        notification.close();
        
        // Manejar navegación basada en el tipo de notificación
        if (data?.type) {
          this.handleNotificationClick(data);
        }
      };

      // Auto-cerrar después de 5 segundos
      setTimeout(() => {
        notification.close();
      }, 5000);
    }
  }

  /**
   * Maneja el clic en una notificación
   */
  private handleNotificationClick(data: NotificationData): void {
    switch (data.type) {
      case 'new_order':
        // Navegar a la página de órdenes
        console.log('🔄 Navegando a nueva orden:', data.orderId);
        // window.location.href = `/orders/${data.orderId}`;
        break;
        
      case 'order_update':
        // Navegar a la orden actualizada
        console.log('🔄 Navegando a orden actualizada:', data.orderId);
        // window.location.href = `/orders/${data.orderId}`;
        break;
        
      case 'waiter_request':
        // Navegar a la mesa con la solicitud
        console.log('🔄 Navegando a solicitud de mesa:', data.tableId);
        // window.location.href = `/tables/${data.tableId}`;
        break;
        
      default:
        console.log('🔄 Tipo de notificación no manejado:', data.type);
    }
  }

  /**
   * Configura un callback para manejar notificaciones
   */
  onNotification(callback: (notification: PushNotification, data: NotificationData) => void): void {
    this.notificationCallback = callback;
  }

  /**
   * Verifica si las notificaciones están habilitadas
   */
  isEnabled(): boolean {
    // Verificar si la API Notification está disponible
    if (typeof Notification === 'undefined') {
      console.warn('⚠️ API Notification no disponible en este navegador');
      return false;
    }
    return Notification.permission === 'granted' && this.isInitialized;
  }

  /**
   * Obtiene el estado de permisos de notificación
   */
  getPermissionStatus(): NotificationPermission {
    // Verificar si la API Notification está disponible
    if (typeof Notification === 'undefined') {
      console.warn('⚠️ API Notification no disponible en este navegador');
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Limpia el servicio de notificaciones
   */
  cleanup(): void {
    this.notificationCallback = undefined;
    this.isInitialized = false;
    console.log('🧹 Servicio de notificaciones limpiado');
  }
}

// Instancia singleton del servicio
export const notificationService = new NotificationService();

// Hook personalizado para usar notificaciones en componentes React
export const useNotifications = () => {
  const requestPermission = (userId?: string) => notificationService.requestPermission(userId);
  const isEnabled = () => notificationService.isEnabled();
  const getPermissionStatus = () => notificationService.getPermissionStatus();
  const onNotification = (callback: (notification: PushNotification, data: NotificationData) => void) => {
    notificationService.onNotification(callback);
  };

  return {
    requestPermission,
    isEnabled,
    getPermissionStatus,
    onNotification,
    syncFCMToken: (userId?: string) => notificationService.syncFCMToken(userId),
    clearFCMToken: (userId: string) => notificationService.clearFCMToken(userId)
  };
};

export default notificationService;
